// What the server needs to count visits, repeat use and where people came from, without accounts:
// a random id this phone keeps, and the first link that brought it here (UTM tags, referring site).
import { ls } from './dom.ts';
import { cleanSrc, nextGroupShare } from '../attribution.ts';

const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function deviceId(): string {
  let id = ls.get<string>('did', '');
  if (!/^[A-Za-z0-9_-]{8,40}$/.test(id)) {
    const bytes = crypto.getRandomValues(new Uint8Array(12));
    id = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    ls.set('did', id);
  }
  return id;
}

/** Landing pages that run the $1 test: half their phones see "$1 from every friend" and start with it on. */
export const FEE_TEST_PAGES = ['/l/points', '/l/both'];

/** The first link that brought this phone here. Kept forever; later visits don't overwrite it. */
function firstTouch(): Record<string, string> {
  const kept = ls.get<Record<string, string> | null>('firstTouch', null);
  if (kept) return withFeeArm(kept);
  const q = new URLSearchParams(location.search);
  const ref: Record<string, string> = { landing: location.pathname, at: new Date().toISOString() };
  for (const k of UTM) if (q.get(k)) ref[k] = (q.get(k) as string).slice(0, 100);
  if (document.referrer && !document.referrer.startsWith(location.origin)) ref.referrer = document.referrer.slice(0, 200);
  return withFeeArm(ref);
}

/** A phone that first landed on a test page gets a side once, at random, and keeps it. */
function withFeeArm(ref: Record<string, string>): Record<string, string> {
  if (FEE_TEST_PAGES.includes(ref.landing) && ref.fee !== 'on' && ref.fee !== 'off') {
    ref.fee = crypto.getRandomValues(new Uint8Array(1))[0] < 128 ? 'on' : 'off';
  }
  ls.set('firstTouch', ref);
  return ref;
}

/**
 * The latest promo or referral code a link brought here (?ref=ALEX10). Unlike first touch, a newer
 * code replaces an older one: the person acted on the newest link. Read on every request, not once,
 * because the friend's "Start a split" link opens inside the app without reloading the page.
 */
function latestCode(): string {
  const fromUrl = new URLSearchParams(location.search).get('ref');
  const code = (fromUrl || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
  if (code.length >= 3) ls.set('refCode', code);
  return ls.get<string>('refCode', '');
}

/** How long a "Start a split" tap from someone's dinner still credits that dinner. */
// Matches LOOP_WINDOW_DAYS in src/attribution.ts.
const LOOP_DAYS = 60;

/** The dinner whose "Start a split" link brought this phone here (?from=LUPA42), for 60 days. */
function latestFrom(): string {
  const fromUrl = (new URLSearchParams(location.search).get('from') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  if (fromUrl.length >= 3) ls.set('fromSplit', { code: fromUrl, at: Date.now() });
  const kept = ls.get<{ code: string; at: number } | null>('fromSplit', null);
  return kept && Date.now() - kept.at < LOOP_DAYS * 86_400_000 ? kept.code : '';
}

/** The latest ?src= a link carried, so where a payer came from survives a copy and paste. */
function latestSrc(): string {
  const v = cleanSrc(new URLSearchParams(location.search).get('src'));
  if (v) ls.set('src', v);
  // The group-share dinner: set by a group_share link, cleared by any newer from= or src= link.
  // The server looks up how old that dinner is.
  ls.set('groupShare', nextGroupShare(ls.get<string>('groupShare', ''), location.search, location.pathname));
  return cleanSrc(ls.get<string>('src', ''));
}

const did = deviceId();
const first = firstTouch();
function currentRef(): Record<string, string> {
  const code = latestCode(), from = latestFrom(), src = latestSrc();
  return { ...first, ...(code ? { ref: code } : {}), ...(from ? { from } : {}), ...(src ? { src } : {}), ...(ls.get<string>('groupShare', '') ? { gs: ls.get<string>('groupShare', '') } : {}) };
}

/** This phone's random id: also what decides its group in an A/B test. */
export const deviceIdNow = (): string => did;

/** Keep any code or dinner the current address carries. Called on every screen change. */
export function rememberLink(): void { latestCode(); latestFrom(); latestSrc(); }

/** This phone's side of the $1 test: 'on', 'off', or '' when it didn't land on a test page. */
export const feeArm = (): '' | 'on' | 'off' => (first.fee === 'on' || first.fee === 'off' ? first.fee : '');

/** The code a link brought, to show "ALEX10 applied" on the setup screen. */
export const linkCode = (): string => latestCode();

/** Steps that happen only on the phone (starting a split, finishing the receipt check), for the funnel. */
export function trackStep(type: 'split_started' | 'receipt_checked' | 'optin_shown' | 'survey_shown' | 'landing_cta' | 'loop_tap' | 'link_kept' | 'card_shared' | 'card_prompt_shown' | 'share_card_offered' | 'next_time_cta_shown' | 'group_share_tapped' | 'group_share_completed' | 'share_tapped' | 'returned_after_pay' | 'sms_return' | 'return_page_paid' | 'return_page_code_shared' | 'return_page_homescreen_opened' | 'return_page_homescreen_installed' | 'credits_collect_tapped' | 'credits_code_sent' | 'credits_claimed', data: Record<string, string | number> = {}): void {
  try {
    void fetch('/api/track', {
      method: 'POST', keepalive: true,
      headers: { ...trackHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify({ type, ...data }),
    }).catch(() => {});
  } catch { /* counting is never worth an error */ }
}

/** Report a step once per phone per key (a card shown on a split, say), not on every redraw. */
export function trackOnce(key: string, type: Parameters<typeof trackStep>[0], data: Record<string, string | number> = {}): void {
  if (ls.get<boolean>('seen:' + key, false)) return;
  ls.set('seen:' + key, true);
  trackStep(type, data);
}

/** Sent with every request to the server. */
export function trackHeaders(): Record<string, string> {
  return { 'x-device': did, 'x-ref': encodeURIComponent(JSON.stringify(currentRef())), 'x-path': location.pathname.replace(/^\/(p|s)\/.+$/, '/$1/:code') };
}
